import type { KyInstance } from 'ky';

import { ensureError, isDefined } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { MergePurgeParameters } from './types/merge-purge-parameters.type';
import type { MultisBreakdown, MultisBreakdownRaw } from './types/multis-breakdown.type';

/**
 * Fetches the multis breakdown report from the API using the provided KyInstance and search parameters.
 *
 * @param instance - The KyInstance used to make the HTTP request.
 * @param searchParams - The parameters for the merge-purge operation.
 * @returns A promise that resolves to an object containing the breakdown data,
 * dynamic column count, and any failure information.
 * @throws Throws an error if the request fails or if an exception occurs during processing.
 */
export const getMultisBreakdown = async (instance: KyInstance, searchParams: MergePurgeParameters) => {
  try {
    const response = await instance.get<OperationResultWithData<MultisBreakdownRaw[]>>(
      Uri.reports.mergePurge.multisBreakdown,
      {
        searchParams,
      }
    );

    const dynamicColumnCount = response.headers.get('x-multis-count');
    const result = await response.json();

    if (result.failure) {
      return result;
    }

    return {
      ...result,
      data: (result.data ?? []).map((raw) => {
        const { breakdown, ...rest } = raw;

        return {
          ...breakdown,
          ...rest,
        };
      }) as MultisBreakdown[],
      dynamicColumnCount: isDefined(dynamicColumnCount) ? Number(dynamicColumnCount) : undefined,
    };
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
